import React, { useState } from "react";
import DefaultModal from "../../default-modal/DefaultModal";
import { types } from "../../../helpers/eventEmitter";
import ModalHeader from "../../modal-header/ModalHeader";
import { Box, Container } from "@mui/material";
import styles from "../Modals.styles";
import ModalFooter from "../../modal-footer/ModalFooter";
import { modalTypes } from "../../../../constants/field-constants";
import { useDispatch, connect } from "react-redux";
import StyledTextField from "../../../styled-components/StyledTextField";
import { selectSuperAdmin } from "../../../../../modules/super-admin/selectors";
import {
  createNewLabelRequest,
  createNewTagRequest,
} from "../../../../../modules/super-admin/actions";
import AlertService from "../../../../services/notification.service";
import { notificationMessage } from '../../../../constants/notication-constants';
import { removeInvalidCharacters, filterAllowedChars, isValidLatinName } from '../../../../utils';
import type { RootState } from '../../../../../reducers';

type TypeCreate = "tagCreate" | "labelCreate" | null;

const LabelTagCreateModal = (props: any) => {
  const dispatch = useDispatch();
  const { labelsList, tagsList } = props;
  const [nameError, setNameError] = useState<string | null>(null);
  const [typeCreate, setTypeCreate] = useState<TypeCreate>(null);
  const [newName, setNewName] = useState("");

  const handleOpen = (type: any) => {
    setTypeCreate(type);
  };

  const handleClearForm = () => {
    setNewName("");
    setNameError(null);
  };

  const handleChange = (
    type: string,
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const raw = e.target.value;
    if (type === "name") {
      const filtered =  filterAllowedChars(raw);
      if (raw !== filtered) {
        setNameError(notificationMessage.ALLOWED_CHARACTERS_ERROR);
      } else {
        setNameError(null);
      }
      setNewName(filtered);
    }

  };

  const handleBlur = () => {
    const removedCharacters = removeInvalidCharacters(newName);
    setNewName(removedCharacters);
    if (removedCharacters && !isValidLatinName(removedCharacters)) {
      setNameError(notificationMessage.ALLOWED_CHARACTERS_ERROR);
    }
  }

  const handleSave = (type: string, name: string, closeModal: any) => {
    const safeName: string = removeInvalidCharacters(name);
    if (!safeName) {
      setNameError("Name is required");
      return;
    }
    if (!isValidLatinName(safeName)) {
      setNameError(notificationMessage.ALLOWED_CHARACTERS_ERROR);
      return;
    }
    setNewName(safeName);
    const nameMatch =
      typeCreate === "tagCreate"
        ? tagsList.filter((tag: any) => tag.name.toLowerCase() === safeName.toLowerCase())
        : labelsList.filter((label: any) => label.name.toLowerCase() === safeName.toLowerCase());
    if (
      type === modalTypes.SAVE &&
      typeCreate === "labelCreate" &&
      nameMatch.length === 0 &&
      !!safeName
    ) {
      dispatch(createNewLabelRequest({ name: safeName }));
      closeModal();
    }
    if (
      type === modalTypes.SAVE &&
      typeCreate === "tagCreate" &&
      nameMatch.length === 0 &&
      !!safeName
    ) {
      dispatch(createNewTagRequest({ name: safeName }));
      closeModal();
    }
    if (
      type === modalTypes.SAVE &&
      typeCreate === "labelCreate" &&
      nameMatch.length > 0 &&
      !!safeName
    ) {
      AlertService.warning(
        "A label with the same name already exists in the database"
      );
      return;
    }
    if (
      type === modalTypes.SAVE &&
      typeCreate === "tagCreate" &&
      nameMatch.length > 0 &&
      !!safeName
    ) {
      AlertService.warning(
        "A tag with the same name already exists in the database"
      );
      return;
    }
    handleClearForm();
  };

  return (
    <>
      <DefaultModal
        onOpen={handleOpen}
        onClose={handleClearForm}
        nameEvent={types.openLabelTagCreateModal}
      >
        {({ closeModal }) => (
          <>
            <ModalHeader
              title={typeCreate === "tagCreate" ? "CREATE TAG" : "CREATE LABEL"}
            />

            <Container sx={styles.formHolder}>
              <Box
                component="form"
                autoComplete="off"
                sx={styles.dataApp}
              >
                <StyledTextField
                  fullWidth
                  required
                  id="label-tag-name"
                  name="labelTagName"
                  autoComplete="off"
                  slotProps={{
                    htmlInput: {
                      inputMode: "text",
                    },
                  }}
                  error={Boolean(nameError)}
                  helperText={nameError || " "}
                  label={typeCreate === "tagCreate" ? "Name tag" : "Name label"}
                  margin="dense"
                  onChange={(e) => handleChange("name", e)}
                  onBlur={handleBlur}
                  value={newName || ""}
                />
              </Box>
            </Container>
            <ModalFooter
              closeModal={closeModal}
              type={modalTypes.CREATE}
              onSave={() => handleSave(modalTypes.SAVE, newName, closeModal)}
            />
          </>
        )}
      </DefaultModal>
    </>
  );
};

function mapStateToProps(state: RootState) {
  const { labelsList, tagsList } = selectSuperAdmin(state);
  return { labelsList, tagsList };
}

export default connect(mapStateToProps)(LabelTagCreateModal);
